/**
 * SemLandingLayout — a lean shell used exclusively for Google Ads SEM
 * landing pages (currently only `/medical-consultation`).
 *
 * Removes:
 *   • Full site navigation (only logo + phone)
 *   • Sticky consultation bar (the page has its own primary CTA)
 *   • Exit-intent modal
 *   • Chat widget
 *   • Marketing footer with all internal links (kept a compact compliance
 *     footer instead, no navigation-inducing anchors that would leak users
 *     away from the single primary conversion goal)
 *
 * Keeps:
 *   • Logo + phone number in header (both drive conversions, low distraction)
 *   • FloatingCallButton (mobile tap-to-call)
 *   • Toaster (form-feedback)
 *
 * This satisfies the "Minimal navigation / One primary conversion goal"
 * Google Ads best practices in the SEM landing page spec.
 */
import { Outlet, Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { FloatingCallButton } from "./PhoneCTA";

export default function SemLandingLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-[color:var(--vv-cream,#f6f5f1)]">
      <header
        data-testid="sem-header"
        className="bg-white border-b border-stone-200"
      >
        <div className="vv-container flex items-center justify-between py-3">
          <Link to="/medical-consultation" className="flex items-center gap-3" data-testid="sem-logo">
            <img
              src="/vv-logo-gold.svg"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              alt=""
              className="h-9 w-9"
            />
            <div className="leading-tight">
              <div className="font-display text-lg text-stone-900">MedicalPeptides<span className="text-[color:var(--vv-gold-deep)]">.ai</span></div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-semibold">Powered by VidaVital Medical</div>
            </div>
          </Link>
          <a
            href="tel:+18887738677"
            data-testid="sem-header-phone"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--vv-ink)] text-white font-semibold px-4 py-2 text-sm hover:opacity-95 transition"
          >
            <Phone size={15} /> <span className="hidden sm:inline">(888) 773-8677</span>
          </a>
        </div>
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer
        data-testid="sem-footer"
        className="bg-[color:var(--vv-ink)] text-stone-300 mt-0"
      >
        <div className="vv-container py-8 text-xs leading-relaxed flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            © {new Date().getFullYear()} VidaVital Medical · MedicalPeptides.ai · A self-pay practice. Insurance not accepted for these services.
          </div>
          <div className="text-stone-400">
            Educational information only — not medical advice. Every patient is evaluated by a licensed medical provider.
          </div>
        </div>
      </footer>

      <FloatingCallButton />
    </div>
  );
}
